import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type {
  Counts,
  Direction,
  PerStageCounts,
  ResultCode,
  ScanAttendeeSnapshot,
  Stage
} from "@attendik/types/schemas";

import { api, ApiError } from "../api/client.js";
import { CameraScanner } from "../components/CameraScanner.js";
import { DirectionToggle } from "../components/DirectionToggle.js";
import { OfflineBanner } from "../components/OfflineBanner.js";
import { RoomCount } from "../components/RoomCount.js";
import {
  ScanFlash,
  type FlashAttendee,
  type FlashResult
} from "../components/ScanFlash.js";
import { useAuth } from "../store/auth.js";
import { playOk, playRejected, playWarning } from "../utils/audio.js";

// How long the result flash stays visible. Originally 1500ms per spec
// §4.3; bumped to 2500ms on operator feedback so the snapshot
// (name + company + groups) and the new rejection reason line stay
// legible long enough on a busy door.
const FLASH_DURATION_MS = 2500;

// How long the camera stays paused after a scan. Kept at the original
// 1500ms so door throughput is unchanged and the backend's 3-second
// duplicate-scan window has a comfortable margin. If a new scan
// arrives between PAUSE_END and FLASH_DURATION_MS, the new flash
// simply replaces the old one — the operator sees the latest scan.
const CAMERA_PAUSE_MS = 1500;

// Maximum number of characters of the raw decoded QR text we surface
// under the "Invalid QR code" flash. A long URL or base64 payload would
// otherwise overflow the overlay on a phone viewport.
const REASON_MAX_LEN = 64;

function truncateReason(text: string, max = REASON_MAX_LEN): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) {
    return trimmed;
  }
  return `${trimmed.slice(0, max - 1)}…`;
}

// Live counts are re-polled on the same cadence as the monitor board so scans
// by other operators show up promptly on the scanner's in-room counter too.
const ACTIVE_COUNTS_POLL_MS = 3000;
const HIDDEN_COUNTS_POLL_MS = 10000;

// CR-03 follow-up: the camera may decode a non-UUID QR (e.g. a URL, a number,
// or a stale ticket from another system). We reject those client-side with a
// clear flash instead of round-tripping a guaranteed 400 to the backend.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// CR-04. Stage-mode loading states the screen renders out before the
// camera/toggle/counter become active.
type StageMode =
  | { kind: "loading" }
  // Event is not staged — pre-CR-04 path. Camera + counts at event level.
  | { kind: "none" }
  // Event is staged. `stage` is the selected stage; the URL ?stage param
  // is the source of truth and survives reloads.
  | { kind: "staged"; stage: Stage }
  // Event is staged but the operator has zero assigned stages. Empty-state
  // screen — no camera, no toggle.
  | { kind: "empty" };

// Maps a backend result_code + a flag for the stage window side to a flash.
// DUPLICATE_SCAN is intentionally silent — no flash, no sound.
function flashFor(resultCode: ResultCode, stagePastWindow: boolean): FlashResult | null {
  switch (resultCode) {
    case null:
      return "OK";
    case "ALREADY_IN":
      return "WARNING_ALREADY_IN";
    case "ALREADY_OUT":
      return "WARNING_ALREADY_OUT";
    case "NOT_REGISTERED":
      return "REJECTED";
    // CR-08. Attendee is not registered for this stage's Meetpoint program.
    case "STAGE_NOT_ELIGIBLE":
      return "STAGE_NOT_ELIGIBLE";
    case "DUPLICATE_SCAN":
      return null;
    // CR-04 result codes.
    case "STAGE_FULL":
      return "WARNING_STAGE_FULL";
    case "STAGE_NOT_ACTIVE":
      return stagePastWindow
        ? "WARNING_STAGE_NOT_ACTIVE_AFTER"
        : "WARNING_STAGE_NOT_ACTIVE_BEFORE";
    // STAGE_TRANSITION_OUT is never the visible outcome of a scan — the
    // operator at door B sees the new ENTER's OK flash, not the transition
    // EXIT (which is an audit-only row).
    case "STAGE_TRANSITION_OUT":
      return "OK";
    default:
      return null;
  }
}

function playCue(flash: FlashResult): void {
  // Each audio helper is already try/catch-wrapped internally.
  if (flash === "OK") {
    playOk();
  } else if (flash.startsWith("WARNING_")) {
    playWarning();
  } else {
    playRejected();
  }
}

// The operator's main working screen: direction toggle, live camera, and a
// full-screen colour flash for each scan result.
//
// CR-03 layout amendments to spec §4.3 (the locked v1.0 layout):
//   1. Top bar is two rows: event name on row 1, operator + logout on row 2.
//   2. ENTER/EXIT toggle buttons show running enter_count / exit_count.
//   3. Camera viewfinder is forced to 1:1 (square).
//   4. Page never scrolls vertically (100dvh, overflow hidden, no overscroll).
//   5. In-room counter sits clear of the iOS Safari bottom URL bar via
//      env(safe-area-inset-bottom) plus extra padding.
//   6. ScanFlash WARNING shows the specific reason ("Already in room" /
//      "Already exited") instead of the generic "WARNING".
//
// CR-04 amendments:
//   * On mount, fetch the operator's assigned stages for this event.
//     0 stages → empty state; 1 → auto-select; 2+ → redirect to the picker.
//   * Selected stage_id rides on the URL `?stage=` so reloads preserve it.
//   * Top bar shows "Event · Stage" when a stage is selected.
//   * Direction toggle counts use per-operator `my_*_count` (event-wide
//     when no stage, per-stage when staged).
//   * In-room counter is stage-scoped when staged.
//   * Two new yellow flash states (STAGE_FULL / STAGE_NOT_ACTIVE).
export function ScanPage(): JSX.Element {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stageQueryParam = searchParams.get("stage");
  const { operator, logout } = useAuth();

  const [direction, setDirection] = useState<Direction>("ENTER");
  const [flash, setFlash] = useState<FlashResult | null>(null);
  // CR-07. Identifying snapshot rendered under the flash label. Cleared
  // back to null at the same time as the flash fades out.
  const [flashAttendee, setFlashAttendee] = useState<FlashAttendee | null>(
    null
  );
  // Optional single-line reason rendered under the flash label on the
  // red rejection paths — badge UUID for REJECTED, raw decoded text for
  // INVALID_QR. Cleared with the flash.
  const [flashReason, setFlashReason] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const [online, setOnline] = useState<boolean>(() => navigator.onLine);
  const [eventName, setEventName] = useState<string | null>(null);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [stageMode, setStageMode] = useState<StageMode>({ kind: "loading" });

  // Two separate timers: pause clears earlier (camera reactivates), flash
  // hides later (operator has more time to read the snapshot). Both are
  // cleared on unmount or when a new scan supersedes the previous flash.
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pauseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const goOnline = (): void => setOnline(true);
    const goOffline = (): void => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    setOnline(navigator.onLine);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (flashTimer.current) {
        clearTimeout(flashTimer.current);
      }
      if (pauseTimer.current) {
        clearTimeout(pauseTimer.current);
      }
    };
  }, []);

  // CR-04 stage resolution. Runs once per (eventId, stageQueryParam).
  //
  // Branches:
  //   * URL has ?stage=… → trust the picker, load that single stage's
  //     metadata. If it isn't in the operator's assignments, fall back to
  //     the regular discovery flow.
  //   * Otherwise → list the operator's stages and decide:
  //     0 stages with event staged → "empty"; 1 → auto-select via URL replace;
  //     2+ → navigate to the picker. If the event isn't staged at all
  //     (`getStages` returns []) and we're not in the empty branch, this is
  //     the pre-CR-04 path: kind = "none".
  useEffect(() => {
    if (!eventId) {
      return;
    }
    let cancelled = false;

    async function resolve(): Promise<void> {
      try {
        const mine = await api.getStages(eventId!, true);
        if (cancelled) return;

        if (stageQueryParam) {
          const match = mine.find((s) => s.id === stageQueryParam);
          if (match) {
            setStageMode({ kind: "staged", stage: match });
            return;
          }
          // URL stage doesn't belong to this operator (or doesn't exist any
          // more). Drop the param and re-resolve via the regular flow.
          navigate(`/scan/${eventId}`, { replace: true });
          return;
        }

        // No stage in the URL — does the event have stages at all?
        const allStages = await api.getStages(eventId!, false);
        if (cancelled) return;

        if (allStages.length === 0) {
          // Pre-CR-04 path: event has no stages, scan at event level.
          setStageMode({ kind: "none" });
          return;
        }
        if (mine.length === 0) {
          // Event is staged but operator has no door.
          setStageMode({ kind: "empty" });
          return;
        }
        if (mine.length === 1) {
          // Auto-select via URL replace so the search param is the source
          // of truth on reload.
          navigate(`/scan/${eventId}?stage=${mine[0]!.id}`, { replace: true });
          return;
        }
        // 2+ assigned — to the picker.
        navigate(`/scan/${eventId}/select-stage`, { replace: true });
      } catch {
        // Likely 401 — the api client already redirected to /login. Or a
        // transient network error; the user can retry. Fall through to the
        // pre-CR-04 path so the screen at least renders.
        if (!cancelled) {
          setStageMode({ kind: "none" });
        }
      }
    }

    void resolve();
    return () => {
      cancelled = true;
    };
  }, [eventId, stageQueryParam, navigate]);

  // Resolve the event name for the top bar. GET /events is the operator's
  // assigned-event list; this event is one of them. Non-critical — on failure
  // the top bar keeps its "Event" placeholder.
  useEffect(() => {
    if (!eventId) {
      return;
    }
    let cancelled = false;
    api
      .getEvents()
      .then((events) => {
        const match = events.find((event) => event.id === eventId);
        if (!cancelled && match) {
          setEventName(match.name);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  // Live occupancy counts. Refreshed after every scan and polled so other
  // operators' scans on this event show up too. Failures are silent — counts
  // are informational and must never disrupt scanning.
  const refreshCounts = useCallback((): void => {
    if (!eventId) {
      return;
    }
    api
      .getCounts(eventId)
      .then(setCounts)
      .catch(() => undefined);
  }, [eventId]);

  useEffect(() => {
    refreshCounts();

    let interval: ReturnType<typeof setInterval>;
    const arm = (): void => {
      clearInterval(interval);
      interval = setInterval(
        refreshCounts,
        document.visibilityState === "hidden"
          ? HIDDEN_COUNTS_POLL_MS
          : ACTIVE_COUNTS_POLL_MS
      );
    };
    arm();

    const onVisibility = (): void => {
      arm();
      if (document.visibilityState !== "hidden") {
        refreshCounts();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refreshCounts]);

  // CR-04. Pull the per-stage row out of counts.per_stage[] for the
  // selected stage. The DirectionToggle and the RoomCount use this when a
  // stage is selected; without one they fall back to event-wide values.
  const selectedStageId =
    stageMode.kind === "staged" ? stageMode.stage.id : null;
  const stageCounts: PerStageCounts | null = useMemo(() => {
    if (!selectedStageId || !counts) return null;
    return counts.per_stage.find((s) => s.stage_id === selectedStageId) ?? null;
  }, [counts, selectedStageId]);

  // The two button labels — per CR-04 these are the operator's OWN counts
  // (my_*), per-stage when a stage is picked.
  const enteredCount = stageCounts
    ? stageCounts.my_enter_count
    : counts?.my_enter_count;
  const exitedCount = stageCounts
    ? stageCounts.my_exit_count
    : counts?.my_exit_count;

  // Room counter — event-wide synced number, per-stage when staged.
  const roomCount = stageCounts
    ? stageCounts.currently_in
    : counts?.currently_in ?? null;

  const showResult = useCallback(
    (
      resultFlash: FlashResult | null,
      // CR-07. Identifying snapshot rendered alongside the flash. Pass
      // null/undefined for transport-error flashes (INVALID_QR /
      // CONNECTION_ERROR) and for REJECTED, where no attendee was
      // resolved.
      attendee?: ScanAttendeeSnapshot | null,
      // Optional reason line — caller-provided string surfaced under
      // the label (badge UUID for REJECTED, raw decoded text for
      // INVALID_QR). Hidden when null/empty.
      reason?: string | null
    ): void => {
      // Pause the camera regardless so a code is not re-read immediately.
      setPaused(true);
      if (resultFlash) {
        setFlash(resultFlash);
        setFlashAttendee(attendee ?? null);
        setFlashReason(reason ?? null);
        playCue(resultFlash);
      }
      // Two independent timers: the camera reactivates at CAMERA_PAUSE_MS
      // (door throughput unchanged), but the flash overlay holds for
      // FLASH_DURATION_MS so the operator has time to read the snapshot
      // and any reason line. If a new scan lands between the two, its
      // showResult call will clear and reschedule both timers.
      if (flashTimer.current) {
        clearTimeout(flashTimer.current);
      }
      if (pauseTimer.current) {
        clearTimeout(pauseTimer.current);
      }
      pauseTimer.current = setTimeout(() => {
        setPaused(false);
        pauseTimer.current = null;
      }, CAMERA_PAUSE_MS);
      flashTimer.current = setTimeout(() => {
        setFlash(null);
        setFlashAttendee(null);
        setFlashReason(null);
        flashTimer.current = null;
      }, FLASH_DURATION_MS);
    },
    []
  );

  const handleScan = useCallback(
    async (badgeId: string): Promise<void> => {
      // Guard: scanning is blocked while offline.
      if (!navigator.onLine) {
        return;
      }
      if (!eventId) {
        return;
      }
      // Defensive: in staged mode without a selected stage, scanning is
      // blocked. The UI prevents this (loading / empty / picker states) but
      // the camera can race ahead of the resolution effect.
      if (stageMode.kind === "loading" || stageMode.kind === "empty") {
        return;
      }
      // CR-03 follow-up: validate the QR text *before* hitting the server. The
      // camera will happily decode any QR — a URL, a number, a stale ticket —
      // and submitting that would either 400 or (worse) muddy the scan log.
      if (!UUID_RE.test(badgeId)) {
        // Surface the raw decoded text under the label so the operator
        // can see what was just read (e.g. a URL, a number). Truncated
        // to keep the overlay legible on a phone.
        showResult("INVALID_QR", null, truncateReason(badgeId));
        return;
      }

      // CR-04. Compute the "past window" flag from the selected stage's
      // active_to so STAGE_NOT_ACTIVE can pick the right wording — "Stage
      // closed" vs. "Stage not active yet".
      const stagePastWindow =
        stageMode.kind === "staged" && stageMode.stage.active_to !== null
          ? Date.now() > new Date(stageMode.stage.active_to as string).getTime()
          : false;

      try {
        const stageId =
          stageMode.kind === "staged" ? stageMode.stage.id : undefined;
        const result = await api.submitScan(eventId, badgeId, direction, stageId);
        // CR-07. The scan response now carries an identifying snapshot
        // whenever a row was resolved. Forward it to the flash so the
        // operator sees who was just scanned. For NOT_REGISTERED the
        // server resolved no attendee — surface the scanned UUID as
        // the reason line instead so the operator can read it back.
        const isNotRegistered = result.result_code === "NOT_REGISTERED";
        showResult(
          flashFor(result.result_code, stagePastWindow),
          result.attendee ?? null,
          isNotRegistered ? badgeId : null
        );
        refreshCounts();
      } catch (error) {
        // Map each failure mode to a flash the operator can act on. Before
        // CR-03 every error fell through to "REJECTED" — misleading, because
        // REJECTED specifically means "not registered for this event".
        if (error instanceof ApiError) {
          if (error.status === 401) {
            // Handled by the client (redirect to /login) — no flash.
            return;
          }
          if (error.status === 0) {
            // Network failure / fetch threw.
            showResult("CONNECTION_ERROR");
            return;
          }
          if (error.status === 400) {
            // Backend rejected the badge_id format or a CR-04 stage rule.
            // The CR-04 errors should be impossible from this UI (we only
            // send stage_id when the event has stages), but cover them.
            // Surface the badge UUID so the operator can read it back.
            showResult("INVALID_QR", null, truncateReason(badgeId));
            return;
          }
        }
        // 5xx, 403/404/409, or anything unexpected — same "something wrong,
        // try again" message.
        showResult("CONNECTION_ERROR");
      }
    },
    [direction, eventId, showResult, refreshCounts, stageMode]
  );

  const handleLogout = useCallback(async (): Promise<void> => {
    try {
      await api.logout();
    } catch {
      // Logout is best-effort — clear the local session regardless.
    }
    logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  // CR-04 empty-state branch. Operator is assigned to an event with stages
  // but to no specific stage. No camera, no toggle.
  if (stageMode.kind === "empty") {
    return (
      <div className="flex h-[100dvh] w-full flex-col overflow-hidden bg-slate-950 text-white">
        <OfflineBanner />
        <header className="flex flex-col bg-slate-900 px-4 py-2">
          <div className="truncate text-base font-semibold">
            {eventName ?? "Event"}
          </div>
          <div className="mt-1 flex items-center justify-between">
            <span className="truncate text-sm text-slate-300">
              Operator: {operator?.name ?? ""}
            </span>
            <button
              type="button"
              onClick={() => void handleLogout()}
              aria-label="Log out"
              className="ml-3 shrink-0 rounded bg-slate-700 px-3 py-1 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              Logout
            </button>
          </div>
        </header>
        <main className="flex flex-1 items-center justify-center p-8 text-center">
          <p className="max-w-sm text-base text-slate-300" data-testid="no-stages">
            No stages assigned. Ask your admin to assign you to a stage.
          </p>
        </main>
      </div>
    );
  }

  // Top-bar event-name slot. When a stage is selected, show "Event · Stage"
  // and make the stage half a link back to the picker (only when 2+ stages
  // are available — but that's the only way the picker exists at all).
  const topBarEventLabel = (() => {
    const eventLabel = eventName ?? "Event";
    if (stageMode.kind !== "staged") {
      return <span className="break-words">{eventLabel}</span>;
    }
    return (
      <span className="break-words">
        {eventLabel}
        <span className="text-slate-400"> · </span>
        <button
          type="button"
          onClick={() => navigate(`/scan/${eventId}/select-stage`)}
          className="break-words text-left underline decoration-slate-500 underline-offset-2 hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400"
          data-testid="stage-switch"
        >
          {stageMode.stage.name}
        </button>
      </span>
    );
  })();

  return (
    // h-[100dvh] uses the *dynamic* viewport height — iOS Safari's URL bar
    // expands and collapses; vh stays fixed and would cause the page to grow
    // taller than the screen. overflow-hidden + overscroll-none kill the
    // scroll-bounce operators complained about during the first live test.
    <div className="flex h-[100dvh] w-full flex-col overflow-hidden overscroll-none bg-slate-950">
      <OfflineBanner />

      {/* Top bar — two rows.
          Row 1: event name (CR-04 appends · stage when staged).
          Row 2: operator cluster (left) + logout (right). */}
      <header className="flex flex-col bg-slate-900 px-4 py-2">
        <div className="text-base font-semibold leading-snug text-white">
          {topBarEventLabel}
        </div>
        <div className="mt-1 flex items-center justify-between">
          <span
            className="truncate text-sm text-slate-300"
            data-testid="operator-label"
          >
            Operator: {operator?.name ?? ""}
          </span>
          <button
            type="button"
            onClick={() => void handleLogout()}
            aria-label="Log out"
            className="ml-3 shrink-0 rounded bg-slate-700 px-3 py-1 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            Logout
          </button>
        </div>
      </header>

      {/* Direction toggle — each button shows the OPERATOR's own running
          count (my_*_count, per CR-04). Per-stage values when a stage is
          selected, event-wide otherwise. */}
      <DirectionToggle
        direction={direction}
        onChange={setDirection}
        enteredCount={enteredCount}
        exitedCount={exitedCount}
      />

      {/* Camera viewfinder + overlays.
          A 1:1 square viewfinder per CR-03 — the rectangular full-bleed
          viewfinder felt awkward at arm's length. The square is anchored
          to the top of <main> (items-start) so on tall portrait phones the
          slack accumulates below it instead of splitting around it. */}
      <main className="relative flex flex-1 items-start justify-center overflow-hidden">
        <div
          data-testid="viewfinder"
          // Exposes the camera-pause state to the E2E suite. Playwright
          // waits on `data-camera-paused="false"` before dispatching the
          // next simulated scan, so step 8 (duplicate scan) lands inside
          // the backend's 3s window independent of any polling cadence.
          // Sits on the viewfinder (always rendered) rather than on the
          // CameraScanner div (replaced by the permission-denied fallback
          // in headless browsers).
          data-camera-paused={String(
            paused || !online || stageMode.kind === "loading"
          )}
          className="relative aspect-square w-full max-h-full max-w-full"
        >
          <CameraScanner
            onScan={(text) => void handleScan(text)}
            paused={paused || !online || stageMode.kind === "loading"}
          />

          {/* CR-03 reticle: four white corner brackets drawn in container
              coordinates so they stay 1:1 regardless of the camera stream. */}
          <div
            data-testid="viewfinder-reticle"
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
          >
            <div className="relative h-[70%] w-[70%]">
              <span className="absolute left-0 top-0 h-6 w-6 border-l-2 border-t-2 border-white/90" />
              <span className="absolute right-0 top-0 h-6 w-6 border-r-2 border-t-2 border-white/90" />
              <span className="absolute bottom-0 left-0 h-6 w-6 border-b-2 border-l-2 border-white/90" />
              <span className="absolute bottom-0 right-0 h-6 w-6 border-b-2 border-r-2 border-white/90" />
            </div>
          </div>

          {/* Dim the viewfinder when offline to show scanning is blocked. */}
          {!online ? (
            <div
              data-testid="camera-blocked"
              className="pointer-events-none absolute inset-0 z-20 bg-black/70"
            />
          ) : null}

          <ScanFlash
            result={flash}
            attendee={flashAttendee}
            reason={flashReason}
          />
        </div>
      </main>

      {/* Live room occupancy — event-wide unless staged. */}
      <RoomCount count={roomCount} />
    </div>
  );
}
